class MyPadConfigurator {
  constructor() {
    this.currentStep = 'start';
    this.selections = {
      display: null,
      type: null,
      feedback: null,
      connectivity: null
    };
    this.productMappings = {};
    this.isAnimating = false;
    this.isRestoring = false;
    this.storageKey = 'mypad-configurator-state';
    
    this.init();
  }

  init() {
    // Load product mappings from the DOM
    this.loadProductMappings();
    
    // Load saved state from localStorage
    this.loadSavedState();
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Initialize progress
    this.updateProgress();
    
    // Initialize dynamic height
    this.initializeDynamicHeight();
    
    // Navigate to the saved step if any
    this.navigateToSavedStep();
  }

  loadProductMappings() {
    const mappingsScript = document.getElementById('mypad-product-mappings');
    if (mappingsScript) {
      try {
        this.productMappings = JSON.parse(mappingsScript.textContent);
        console.log('Product mappings loaded:', this.productMappings);
      } catch (error) {
        console.error('Error parsing product mappings:', error);
      }
    }
  }

  // LocalStorage methods for state persistence
  saveState() {
    const state = {
      currentStep: this.currentStep,
      selections: { ...this.selections },
      timestamp: Date.now()
    };
    
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(state));
      console.log('💾 State saved to localStorage:', state);
    } catch (error) {
      console.error('Error saving state to localStorage:', error);
    }
  }

  loadSavedState() {
    try {
      const savedState = localStorage.getItem(this.storageKey);
      if (savedState) {
        const state = JSON.parse(savedState);
        
        // Check if the saved state is not too old (e.g., 7 days)
        const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
        const isExpired = Date.now() - (state.timestamp || 0) > maxAge;
        
        if (isExpired) {
          console.log('💾 Saved state expired, starting fresh');
          this.clearSavedState();
          return;
        }
        
        // Restore saved state
        this.currentStep = state.currentStep || 'start';
        this.selections = { ...this.selections, ...state.selections };
        
        console.log('💾 State loaded from localStorage:', state);
        console.log('💾 Restored to step:', this.currentStep);
        console.log('💾 Restored selections:', this.selections);
      }
    } catch (error) {
      console.error('Error loading state from localStorage:', error);
      this.clearSavedState();
    }
  }

  clearSavedState() {
    try {
      localStorage.removeItem(this.storageKey);
      console.log('💾 Saved state cleared from localStorage');
    } catch (error) {
      console.error('Error clearing state from localStorage:', error);
    }
  }

  hasSavedState() {
    try {
      const savedState = localStorage.getItem(this.storageKey);
      return savedState !== null;
    } catch (error) {
      return false;
    }
  }

  getSavedState() {
    try {
      const savedState = localStorage.getItem(this.storageKey);
      return savedState ? JSON.parse(savedState) : null;
    } catch (error) {
      console.error('Error getting saved state:', error);
      return null;
    }
  }

  navigateToSavedStep() {
    // Only navigate if we have a saved step that's not 'start'
    if (this.currentStep && this.currentStep !== 'start') {
      console.log('💾 Navigating to saved step:', this.currentStep);
      
      // Set restoring flag to prevent saving during restoration
      this.isRestoring = true;
      
      // Delay navigation to ensure DOM is ready
      setTimeout(() => {
        // Restore visual selections for all completed steps
        this.restoreAllVisualSelections();
        
        // Navigate to the saved step
        this.goToStepSilently(this.currentStep);
        
        // Update progress to reflect restored state
        this.updateProgress();
        
        // If we're restoring to the result step, show the product result
        if (this.currentStep === 'result') {
          console.log('💾 Restoring result step - showing product result');
          this.showStoredResult();
        }
        
        // Clear restoring flag
        this.isRestoring = false;
        
        console.log('💾 State restoration completed');
      }, 100);
    }
  }

  restoreAllVisualSelections() {
    const stepOrder = ['display', 'type', 'feedback', 'connectivity'];
    
    stepOrder.forEach(stepName => {
      if (this.selections[stepName]) {
        this.restoreStepSelections(stepName);
      }
    });
  }

  goToStepSilently(stepName) {
    // Navigate to step without animations or localStorage updates
    const currentActive = document.querySelector('.mypad-step.active');
    const targetStep = document.querySelector(`.mypad-step[data-step="${stepName}"]`);
    
    if (currentActive) {
      currentActive.classList.remove('active');
    }
    
    if (targetStep) {
      targetStep.classList.add('active');
      console.log('💾 Silently navigated to step:', stepName);
    }
    
    // Update container height
    setTimeout(() => {
      this.updateContainerHeight();
    }, 50);
  }

  setupEventListeners() {
    // Start button
    const startButton = document.querySelector('.mypad-next-button[data-next="display"]');
    if (startButton) {
      startButton.addEventListener('click', () => this.goToStep('display'));
    }

    // Option selections
    document.addEventListener('click', (e) => {
      const option = e.target.closest('.mypad-option');
      if (option) {
        this.handleOptionSelection(option);
      }
    });

    // Restart button
    const restartButton = document.querySelector('.mypad-restart-button');
    if (restartButton) {
      restartButton.addEventListener('click', () => this.restart());
    }

    // Progress step clicks
    document.addEventListener('click', (e) => {
      const progressStep = e.target.closest('.mypad-progress__step');
      if (progressStep && progressStep.classList.contains('completed')) {
        this.handleProgressStepClick(progressStep);
      }
    });
  }

  initializeDynamicHeight() {
    // Set initial height
    this.updateContainerHeight();
    
    // Handle window resize
    window.addEventListener('resize', () => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.updateContainerHeight();
      }, 150);
    });
  }

  updateContainerHeight() {
    // Debounce to prevent excessive calls
    clearTimeout(this.heightUpdateTimeout);
    this.heightUpdateTimeout = setTimeout(() => {
      this.performHeightUpdate();
    }, 10);
  }

  performHeightUpdate() {
    const container = document.querySelector('.mypad-configurator__content');
    const activeStep = document.querySelector('.mypad-step.active');
    
    if (!container || !activeStep) return;
    
    // Temporarily make the step visible and measure it
    const originalTransform = activeStep.style.transform;
    const originalOpacity = activeStep.style.opacity;
    const originalPointerEvents = activeStep.style.pointerEvents;
    
    activeStep.style.transform = 'translateX(0)';
    activeStep.style.opacity = '1';
    activeStep.style.pointerEvents = 'auto';
    
    // Force a reflow to ensure styles are applied
    activeStep.offsetHeight;
    
    const stepHeight = this.measureStepHeight(activeStep);
    
    // Restore original styles
    activeStep.style.transform = originalTransform;
    activeStep.style.opacity = originalOpacity;
    activeStep.style.pointerEvents = originalPointerEvents;
    
    // Set container height with proper minimums for mobile
    const isMobile = window.innerWidth <= 768;
    let minHeight;
    
    if (isMobile) {
      // More generous minimum height for mobile, especially for start page
      minHeight = Math.max(stepHeight + 100, 500); // Extra padding for mobile
    } else {
      minHeight = Math.max(stepHeight + 50, 600);
    }
    
    container.style.height = `${minHeight}px`;
    
    console.log(`🎯 Height updated: step=${stepHeight}px, container=${minHeight}px, mobile=${isMobile}`);
  }

  measureStepHeight(stepElement) {
    const content = stepElement.querySelector('.mypad-step__content');
    if (!content) return 500;
    
    // Check if there are any images that might not be loaded
    const images = content.querySelectorAll('img');
    let allImagesLoaded = true;
    
    images.forEach(img => {
      if (!img.complete || img.naturalHeight === 0) {
        allImagesLoaded = false;
        // Set a timeout to update height when image loads
        img.onload = () => {
          setTimeout(() => {
            this.updateContainerHeight();
          }, 50);
        };
      }
    });
    
    // Wait for next frame to ensure layout is stable
    const measureHeight = () => {
      // Get the scroll height which includes all content
      const scrollHeight = content.scrollHeight;
      const offsetHeight = content.offsetHeight;
      
      // Use the larger of the two
      let actualHeight = Math.max(scrollHeight, offsetHeight);
      
      // Get the full height including margins and padding
      const computedStyle = getComputedStyle(content);
      const marginTop = parseInt(computedStyle.marginTop) || 0;
      const marginBottom = parseInt(computedStyle.marginBottom) || 0;
      const paddingTop = parseInt(computedStyle.paddingTop) || 0;
      const paddingBottom = parseInt(computedStyle.paddingBottom) || 0;
      
      let height = actualHeight + marginTop + marginBottom;
      
      // If images aren't loaded, add estimated height for images
      if (!allImagesLoaded) {
        const unloadedImages = content.querySelectorAll('img:not([data-measured])');
        unloadedImages.forEach(img => {
          // Add estimated height for images (adjust based on mobile/desktop)
          const isMobile = window.innerWidth <= 768;
          height += isMobile ? 150 : 200;
          img.setAttribute('data-measured', 'true');
        });
      }
      
      // Add buffer based on device type
      const isMobile = window.innerWidth <= 768;
      const buffer = isMobile ? 60 : 80;
      
      return height + buffer;
    };
    
    return measureHeight();
  }

  handleOptionSelection(option) {
    if (this.isAnimating) return;

    const value = option.dataset.value;
    const currentStepElement = document.querySelector('.mypad-step.active');
    const stepType = currentStepElement.dataset.step;

    // Add selection animation
    option.classList.add('selecting');
    
    setTimeout(() => {
      // Remove selection animation and add selected state
      option.classList.remove('selecting');
      option.classList.add('selected');
      
      // Remove selected state from siblings
      const siblings = option.parentElement.querySelectorAll('.mypad-option');
      siblings.forEach(sibling => {
        if (sibling !== option) {
          sibling.classList.remove('selected');
        }
      });

      // Store selection
      this.selections[stepType] = value;
      console.log('Selection made:', stepType, value);

      // Save state to localStorage
      this.saveState();

      // Navigate to next step after a short delay
      setTimeout(() => {
        this.navigateToNextStep(stepType);
      }, 300);
    }, 150);
  }

  handleProgressStepClick(progressStep) {
    if (this.isAnimating) return;

    const stepName = progressStep.dataset.step;
    if (stepName && stepName !== this.currentStep) {
      this.goToStep(stepName, true); // true indicates going backwards
    }
  }

  navigateToNextStep(currentStepType) {
    const stepOrder = ['start', 'display', 'type', 'feedback', 'connectivity', 'result'];
    const currentIndex = stepOrder.indexOf(currentStepType);
    const nextStep = stepOrder[currentIndex + 1];

    if (nextStep) {
      if (nextStep === 'result') {
        this.showResult();
      } else {
        this.goToStep(nextStep);
      }
    }
  }

  goToStep(stepName, isBackward = false) {
    if (this.isAnimating) return;
    
    this.isAnimating = true;
    
    // Clean up any lingering animation classes before starting new transition
    document.querySelectorAll('.mypad-step').forEach(step => {
      if (step.classList.contains('prev') || step.classList.contains('next')) {
        console.log(`🧹 Cleaning up animation classes from step:`, step.dataset.step);
      }
      step.classList.remove('prev', 'next');
    });
    
    const currentStepElement = document.querySelector('.mypad-step.active');
    const nextStepElement = document.querySelector(`.mypad-step[data-step="${stepName}"]`);

    if (!nextStepElement) {
      console.error('Step not found:', stepName);
      this.isAnimating = false;
      return;
    }

    // Update current step
    const previousStep = this.currentStep;
    this.currentStep = stepName;

    // Save state to localStorage (except during initial restoration)
    if (!this.isRestoring) {
      this.saveState();
    }

    // If going backward, reset all subsequent steps
    if (isBackward) {
      this.resetSubsequentSteps(stepName);
    }

    // Animate transition based on direction
    if (currentStepElement) {
      currentStepElement.classList.remove('active');
      if (isBackward) {
        currentStepElement.classList.add('next'); // For backward animation
      } else {
        currentStepElement.classList.add('prev');
      }
    }

    nextStepElement.classList.remove('prev', 'next');
    nextStepElement.classList.add('active');

    // If going backward, restore previous selections visually
    if (isBackward) {
      this.restoreStepSelections(stepName);
    }

    // Update progress
    this.updateProgress();

    // If going backward, do a final verification that subsequent steps are cleared
    if (isBackward) {
      setTimeout(() => {
        this.verifyBackwardReset(stepName);
      }, 100);
    }

    // Update container height for the new step
    setTimeout(() => {
      this.updateContainerHeight();
    }, 50);

    // Auto-scroll to progress element (delay to let animation start)
    setTimeout(() => {
      this.scrollToProgress();
    }, 150);

    // Clean up classes after animation
    setTimeout(() => {
      document.querySelectorAll('.mypad-step').forEach(step => {
        step.classList.remove('prev', 'next'); // Remove both prev and next classes
      });
      this.isAnimating = false;
      console.log(`✅ Animation cleanup completed for transition to step: ${stepName}`);
    }, 400);
  }

  restoreStepSelections(stepName) {
    console.log(`🔄 Restoring selections for step "${stepName}"`);
    console.log(`🔄 Stored value for step "${stepName}":`, this.selections[stepName]);
    
    // Restore visual selection based on stored data
    const stepElement = document.querySelector(`.mypad-step[data-step="${stepName}"]`);
    if (!stepElement) {
      console.error(`🔄 Step element not found for "${stepName}"`);
      return;
    }

    const options = stepElement.querySelectorAll('.mypad-option');
    const selectedValue = this.selections[stepName];

    console.log(`🔄 Found ${options.length} options in step "${stepName}"`);

    options.forEach((option, index) => {
      option.classList.remove('selected');
      if (option.dataset.value === selectedValue && selectedValue) {
        option.classList.add('selected');
        console.log(`🔄 Restored selection to option ${index + 1} with value "${selectedValue}"`);
      }
    });
  }

  // Reset all steps after the target step when going backwards
  resetSubsequentSteps(targetStepName) {
    console.log(`🔄 Resetting all steps after step "${targetStepName}"`);
    
    const stepOrder = ['start', 'display', 'type', 'feedback', 'connectivity', 'result'];
    const targetIndex = stepOrder.indexOf(targetStepName);
    
    console.log(`🔄 Target step "${targetStepName}" is at index ${targetIndex}`);
    console.log(`🔄 Current selections before reset:`, this.selections);
    
    // Clear user selections for subsequent steps
    const stepsToReset = [];
    for (let i = targetIndex + 1; i < stepOrder.length; i++) {
      const stepName = stepOrder[i];
      if (stepName !== 'start' && stepName !== 'result') {
        console.log(`🔄 About to clear step: ${stepName} (index ${i})`);
        this.clearStepSelection(stepName);
        stepsToReset.push(stepName);
      }
    }
    
    console.log(`🔄 Reset steps: ${stepsToReset.join(', ')}`);
    console.log('🔄 Selections after reset:', this.selections);
    
    // Save updated state to localStorage
    this.saveState();
    
    // Force a small delay to ensure DOM updates are processed
    setTimeout(() => {
      console.log('🔄 Verifying clearing worked...');
      stepsToReset.forEach(stepName => {
        const stepElement = document.querySelector(`.mypad-step[data-step="${stepName}"]`);
        if (stepElement) {
          const selectedOptions = stepElement.querySelectorAll('.mypad-option.selected');
          if (selectedOptions.length > 0) {
            console.warn(`🔄 Step "${stepName}" still has ${selectedOptions.length} selected options!`);
            selectedOptions.forEach(option => option.classList.remove('selected'));
          }
        }
      });
    }, 10);
  }

  // Clear selections for a specific step
  clearStepSelection(stepName) {
    console.log(`🔄 Clearing selection for step "${stepName}"`);
    
    // Clear from selections object
    this.selections[stepName] = null;
    
    // Clear visual selection indicators for the step
    const stepElement = document.querySelector(`.mypad-step[data-step="${stepName}"]`);
    if (stepElement) {
      const options = stepElement.querySelectorAll('.mypad-option');
      console.log(`🔄 Found ${options.length} options in step "${stepName}" to clear`);
      options.forEach((option, index) => {
        if (option.classList.contains('selected')) {
          console.log(`🔄 Removing 'selected' class from option ${index + 1}`);
        }
        option.classList.remove('selected');
      });
    } else {
      console.error(`🔄 Step element not found for "${stepName}"`);
    }
  }

  updateProgress() {
    const steps = document.querySelectorAll('.mypad-progress__step');
    const stepOrder = ['start', 'display', 'type', 'feedback', 'connectivity', 'result'];
    const currentIndex = stepOrder.indexOf(this.currentStep);

    steps.forEach((step, index) => {
      const stepElement = step;
      const stepName = stepOrder[index];
      
      // Remove all states
      stepElement.classList.remove('active', 'completed');
      
      // Mark as completed if:
      // 1. It's before current step, OR
      // 2. It has a stored selection (user has been there before)
      if (index < currentIndex || (stepName !== 'start' && stepName !== 'result' && this.selections[stepName])) {
        stepElement.classList.add('completed');
      }
      
      if (index === currentIndex) {
        stepElement.classList.add('active');
        
        // Auto-scroll to active step on mobile
        if (window.innerWidth <= 480) {
          this.scrollToActiveStep(stepElement);
        }
      }
    });
  }

  scrollToActiveStep(activeStep) {
    const progressContainer = document.querySelector('.mypad-progress');
    if (!progressContainer || !activeStep) return;

    // Calculate position to center the active step
    const containerWidth = progressContainer.offsetWidth;
    const stepLeft = activeStep.offsetLeft;
    const stepWidth = activeStep.offsetWidth;
    
    // Calculate scroll position to center the step
    const scrollLeft = stepLeft - (containerWidth / 2) + (stepWidth / 2);
    
    // Smooth scroll to position
    progressContainer.scrollTo({
      left: Math.max(0, scrollLeft),
      behavior: 'smooth'
    });
  }

  scrollToProgress() {
    const progressElement = document.querySelector('.mypad-progress');
    if (!progressElement) return;

    // Get the top position of the progress element
    const progressTop = progressElement.offsetTop;
    
    // Add offset for fixed header (100px) + extra padding (20px) = 120px total
    const scrollTarget = Math.max(0, progressTop - 120);
    
    // Smooth scroll to the progress element
    window.scrollTo({
      top: scrollTarget,
      behavior: 'smooth'
    });
    
    console.log(`📍 Auto-scrolled to progress element at position ${scrollTarget}px (accounting for 100px fixed header)`);
  }

  // Final verification that backward reset worked properly
  verifyBackwardReset(targetStepName) {
    console.log(`🔄 Final verification for backward reset from step "${targetStepName}"`);
    
    const stepOrder = ['start', 'display', 'type', 'feedback', 'connectivity', 'result'];
    const targetIndex = stepOrder.indexOf(targetStepName);
    
    for (let i = targetIndex + 1; i < stepOrder.length; i++) {
      const stepName = stepOrder[i];
      if (stepName !== 'start' && stepName !== 'result') {
        // Check data consistency
        if (this.selections[stepName] !== null) {
          console.warn(`🔄 Data inconsistency: step "${stepName}" still has selection:`, this.selections[stepName]);
          this.selections[stepName] = null;
        }
        
        // Check visual consistency
        const stepElement = document.querySelector(`.mypad-step[data-step="${stepName}"]`);
        if (stepElement) {
          const selectedOptions = stepElement.querySelectorAll('.mypad-option.selected');
          if (selectedOptions.length > 0) {
            console.warn(`🔄 Visual inconsistency: step "${stepName}" has ${selectedOptions.length} selected options - force clearing`);
            selectedOptions.forEach(option => {
              option.classList.remove('selected');
              console.log(`🔄 Force removed 'selected' class from option in step "${stepName}"`);
            });
          }
        }
      }
    }
    
    console.log(`🔄 Verification complete. Final selections:`, this.selections);
  }

  showResult() {
    // Generate product key from selections
    const productKey = `${this.selections.display}_${this.selections.type}_${this.selections.feedback}_${this.selections.connectivity}`;
    console.log('Looking for product with key:', productKey);
    console.log('Available mappings:', Object.keys(this.productMappings));
    
    const productData = this.productMappings[productKey];
    
    if (productData) {
      this.goToStep('result');
      this.renderProductResult(productData);
      // Save final state to localStorage
      this.saveState();
    } else {
      console.error('No product found for configuration:', this.selections);
      this.showErrorResult();
    }
  }

  showStoredResult() {
    // Check if we have all required selections for showing a result
    if (!this.selections.display || !this.selections.type || 
        !this.selections.feedback || !this.selections.connectivity) {
      console.log('💾 Not all selections available for result restoration, clearing result');
      const resultContainer = document.querySelector('.mypad-result__product');
      if (resultContainer) {
        resultContainer.innerHTML = '';
      }
      return;
    }

    // Generate product key from restored selections
    const productKey = `${this.selections.display}_${this.selections.type}_${this.selections.feedback}_${this.selections.connectivity}`;
    console.log('💾 Restoring product with key:', productKey);
    console.log('💾 Restored selections:', this.selections);
    
    const productData = this.productMappings[productKey];
    
    if (productData) {
      console.log('💾 Found product data for restoration:', productData.product_title);
      this.renderProductResult(productData);
    } else {
      console.error('💾 No product found for restored configuration:', this.selections);
      this.showErrorResult();
    }
  }

  renderProductResult(productData) {
    const resultContainer = document.querySelector('.mypad-result__product');
    
    // Show loading state
    resultContainer.classList.add('loading');
    resultContainer.innerHTML = '';

    // Simulate loading delay for better UX
    setTimeout(() => {
      resultContainer.classList.remove('loading');
      
      resultContainer.innerHTML = `
        <div class="mypad-product-display">
          <div class="mypad-product-display__image">
            <img src="${productData.product_image}" alt="${productData.product_title}" loading="lazy">
          </div>
          <div class="mypad-product-display__info">
            <h3 class="mypad-product-display__title">${productData.product_title}</h3>
            <div class="mypad-product-display__variant">${productData.variant_title}</div>
            <div class="mypad-product-display__description">
              ${this.generateProductDescription()}
            </div>
            <div class="mypad-product-display__actions">
              <a href="${productData.product_url}" class="mypad-button mypad-button--primary">
                Zum Produkt
              </a>
              <button class="mypad-button mypad-button--secondary" onclick="window.addToCartMyPad('${productData.product_handle}', '${productData.variant_title}', this)">
                In den Warenkorb
              </button>
            </div>
                     </div>
         </div>
       `;
       
       // Update height after content is rendered
       setTimeout(() => {
         this.updateContainerHeight();
       }, 100);
    }, 800);
  }

  generateProductDescription() {
    const features = [];
    
    // Add features based on selections
    if (this.selections.display === 'led') {
      features.push('LED-Grafik Display für einfache Bedienung');
    } else if (this.selections.display === 'lcd') {
      features.push('LCD-Farbbildschirm mit detaillierten Anweisungen');
    } else if (this.selections.display === 'touch') {
      features.push('Interaktiver Touchscreen mit erweiterten Funktionen');
    }

    if (this.selections.type === 'manual') {
      features.push('Manuelle Schockauslösung für erfahrene Anwender');
    } else {
      features.push('Automatische Schockabgabe für Laienanwendung');
    }

    if (this.selections.feedback === 'yes') {
      features.push('HLW-Feedback-Sensor für optimale Herzdruckmassage');
    }

    if (this.selections.connectivity === 'yes') {
      features.push('LTE + WIFI Konnektivität für Fernüberwachung');
    }

    return `
      <p>Ihr individuell konfigurierter myPAD Defibrillator mit folgenden Eigenschaften:</p>
      <ul style="text-align: left; margin: 15px 0;">
        ${features.map(feature => `<li>${feature}</li>`).join('')}
      </ul>
      <p>Perfekt abgestimmt auf Ihre Anforderungen und Einsatzgebiete.</p>
    `;
  }

  showErrorResult() {
    this.goToStep('result');
    const resultContainer = document.querySelector('.mypad-result__product');
    
    resultContainer.innerHTML = `
      <div style="text-align: center; padding: 40px;">
        <h3>Entschuldigung!</h3>
        <p>Für diese Konfiguration ist derzeit kein Produkt verfügbar.</p>
        <p>Bitte kontaktieren Sie uns für eine individuelle Beratung.</p>
        <button class="mypad-button mypad-button--primary" onclick="window.location.href='/pages/contact'">
          Kontakt aufnehmen
        </button>
      </div>
    `;
    
    // Update height after content is rendered
    setTimeout(() => {
      this.updateContainerHeight();
    }, 100);
  }



  restart() {
    console.log('💾 Restarting configurator and clearing saved state');
    
    // Clear saved state from localStorage
    this.clearSavedState();
    
    // Reset all selections
    this.selections = {
      display: null,
      type: null,
      feedback: null,
      connectivity: null
    };

    // Remove all selected states
    document.querySelectorAll('.mypad-option.selected').forEach(option => {
      option.classList.remove('selected');
    });

    // Reset all steps
    document.querySelectorAll('.mypad-step').forEach(step => {
      step.classList.remove('active', 'prev');
    });

    // Go back to start
    this.currentStep = 'start';
    document.querySelector('.mypad-step[data-step="start"]').classList.add('active');
    
    // Update progress
    this.updateProgress();

    // Clear result container
    const resultContainer = document.querySelector('.mypad-result__product');
    if (resultContainer) {
      resultContainer.innerHTML = '';
    }
    
    // Update height for start step
    setTimeout(() => {
      this.updateContainerHeight();
    }, 100);
  }
}

// Initialize configurator when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  // Only initialize if the configurator exists on the page
  if (document.querySelector('.mypad-configurator')) {
    new MyPadConfigurator();
  }
});

// Utility function for adding to cart (can be called from the rendered HTML)
window.addToCartMyPad = async function(productHandle, variantTitle, buttonElement) {
  console.log('🛒 MyPad Cart: Starting add to cart process');
  console.log('🛒 MyPad Cart: Product handle:', productHandle);
  console.log('🛒 MyPad Cart: Variant title:', variantTitle);
  
  // Get button element - either passed as parameter or find it via event
  let button = buttonElement;
  if (!button && typeof event !== 'undefined' && event.target) {
    button = event.target;
  }
  if (!button) {
    // Fallback: find the button in the result section
    button = document.querySelector('.mypad-result .mypad-button--secondary');
    console.log('🛒 MyPad Cart: Found button via fallback selector:', !!button);
  }
  
  if (!button) {
    console.error('🛒 MyPad Cart: ❌ Could not find button element');
    return;
  }
  
  console.log('🛒 MyPad Cart: Button element found:', button.textContent);
  
  // Show loading state
  const originalText = button.textContent;
  button.textContent = 'Wird hinzugefügt...';
  button.disabled = true;
  
  try {
    // First, fetch the product data to get variant ID (same as mypad-product.js)
    console.log('🛒 MyPad Cart: Fetching product data...');
    const response = await fetch(`/products/${productHandle}.js`);
    const product = await response.json();
    console.log('🛒 MyPad Cart: Product data fetched:', product.title);
    console.log('🛒 MyPad Cart: Available variants:', product.variants.length);
    
    // Debug: Log all variants with their details
    product.variants.forEach((v, index) => {
      console.log(`🛒 MyPad Cart: Variant ${index}:`, {
        id: v.id,
        title: v.title,
        option1: v.option1,
        option2: v.option2,
        option3: v.option3,
        available: v.available,
        price: v.price
      });
    });
    
    // Find the variant using the same logic as mypad-product.js
    // The variant title should match one of the variant's full title or option values
    console.log('🛒 MyPad Cart: Looking for variant matching:', variantTitle);
    
    const variant = product.variants.find(v => {
      console.log(`🛒 MyPad Cart: Checking variant "${v.title}" against "${variantTitle}"`);
      
      // Match by exact title first
      if (v.title === variantTitle) {
        console.log('🛒 MyPad Cart: ✅ Matched by exact title');
        return true;
      }
      
      // Match by individual options (like mypad-product.js does)
      if (v.option1 === variantTitle) {
        console.log('🛒 MyPad Cart: ✅ Matched by option1');
        return true;
      }
      
      if (v.option2 === variantTitle) {
        console.log('🛒 MyPad Cart: ✅ Matched by option2');
        return true;
      }
      
      if (v.option3 === variantTitle) {
        console.log('🛒 MyPad Cart: ✅ Matched by option3');
        return true;
      }
      
      // Try partial match for titles that might include the option
      if (v.title && v.title.includes(variantTitle)) {
        console.log('🛒 MyPad Cart: ✅ Matched by partial title match');
        return true;
      }
      
      console.log('🛒 MyPad Cart: ❌ No match');
      return false;
    });
    
    if (!variant) {
      console.error('🛒 MyPad Cart: ❌ No variant found for:', variantTitle);
      console.error('🛒 MyPad Cart: Available variant titles:', product.variants.map(v => v.title));
      throw new Error(`Variante "${variantTitle}" nicht gefunden`);
    }
    
    if (!variant.available) {
      console.error('🛒 MyPad Cart: ❌ Variant not available:', variant.title);
      throw new Error(`Variante "${variant.title}" ist nicht verfügbar`);
    }
    
    console.log('🛒 MyPad Cart: ✅ Found variant:', {
      id: variant.id,
      title: variant.title,
      available: variant.available,
      price: variant.price
    });
    
    // Use the EXACT same approach as product-form.js
    console.log('🛒 MyPad Cart: Creating FormData for cart request...');
    const formData = new FormData();
    formData.append('id', variant.id);
    formData.append('quantity', 1);
    
    // Add cart sections for rendering (exactly like product-form.js)
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    console.log('🛒 MyPad Cart: Cart element found:', !!cart);
    
    if (cart && cart.getSectionsToRender) {
      const sections = cart.getSectionsToRender().map((section) => section.id);
      console.log('🛒 MyPad Cart: Cart sections to render:', sections);
      formData.append('sections', sections.join(','));
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement(document.activeElement);
    }
    
    // Debug FormData
    console.log('🛒 MyPad Cart: FormData contents:');
    for (let [key, value] of formData.entries()) {
      console.log(`🛒 MyPad Cart: ${key}: ${value}`);
    }
    
    // Add to cart using the EXACT same config as product-form.js
    console.log('🛒 MyPad Cart: Sending cart add request...');
    
    // Use the same fetchConfig as product-form.js
    const config = window.fetchConfig ? window.fetchConfig('javascript') : {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/javascript'
      }
    };
    
    // Add X-Requested-With header and DELETE Content-Type (critical!)
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type']; // FormData sets its own Content-Type
    
    // Set the FormData body
    config.body = formData;
    
    console.log('🛒 MyPad Cart: Request config:', {
      method: config.method,
      headers: config.headers,
      bodyType: config.body.constructor.name
    });
    
    // Use the exact same URL approach as product-form.js
    const cartAddUrl = window.routes?.cart_add_url || '/cart/add';
    console.log('🛒 MyPad Cart: Using cart add URL:', cartAddUrl);
    console.log('🛒 MyPad Cart: Available routes:', window.routes);
    
    const addResponse = await fetch(cartAddUrl, config);
    console.log('🛒 MyPad Cart: Cart add response status:', addResponse.status);
    console.log('🛒 MyPad Cart: Cart add response headers:', Object.fromEntries(addResponse.headers.entries()));
    
    // Debug: Check response content type and text before parsing
    const responseText = await addResponse.text();
    console.log('🛒 MyPad Cart: Raw response text (first 200 chars):', responseText.substring(0, 200));
    
    let responseData;
    try {
      responseData = JSON.parse(responseText);
      console.log('🛒 MyPad Cart: Cart add response data:', responseData);
    } catch (parseError) {
      console.error('🛒 MyPad Cart: ❌ Failed to parse JSON response:', parseError);
      console.error('🛒 MyPad Cart: Full response text:', responseText);
      throw new Error('Invalid JSON response from cart API');
    }
    
    if (responseData.status) {
      console.error('🛒 MyPad Cart: ❌ Cart add failed:', responseData);
      throw new Error(responseData.description || responseData.message || 'Fehler beim Hinzufügen zum Warenkorb');
    }
    
    console.log('🛒 MyPad Cart: ✅ Successfully added to cart');
    
    // Success feedback
    button.textContent = 'Hinzugefügt! ✓';
    button.style.background = '#4CAF50';
    
    // Update cart display using theme's methods (same as product-form.js)
    if (cart && cart.renderContents) {
      console.log('🛒 MyPad Cart: Updating cart display...');
      cart.renderContents(responseData);
    }
    
    // Open cart drawer like in mypad product page
    if (cart) {
      console.log('🛒 MyPad Cart: Opening cart drawer...');
      if (cart.open) {
        cart.open();
      } else if (cart.show) {
        cart.show();
      } else if (cart.classList) {
        cart.classList.add('active');
      }
      
      // Also try triggering the cart drawer button
      const cartDrawerButton = document.querySelector('[data-cart-drawer]') || 
                               document.querySelector('#cart-icon-bubble') ||
                               document.querySelector('.cart-count-bubble');
      if (cartDrawerButton && cartDrawerButton.click) {
        console.log('🛒 MyPad Cart: Clicking cart drawer button...');
        setTimeout(() => cartDrawerButton.click(), 100);
      }
    }
    
    // Publish cart update events for theme compatibility (same as product-form.js)
    if (window.publish && window.PUB_SUB_EVENTS) {
      console.log('🛒 MyPad Cart: Publishing cart update event...');
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, {source: 'mypad-configurator', productVariantId: variant.id});
    }
    
    // Also try additional cart update methods
    if (window.theme && window.theme.cartUpdateCallback) {
      console.log('🛒 MyPad Cart: Calling theme cart update callback...');
      window.theme.cartUpdateCallback();
    }
    
    // Dispatch custom events that might be listened to
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { variant_id: variant.id, quantity: 1 }
    }));
    
    // Try additional cart drawer opening methods
    console.log('🛒 MyPad Cart: Trying additional cart opening methods...');
    
    // Method 1: Check for cart drawer web component
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.open) {
      console.log('🛒 MyPad Cart: Opening cart-drawer web component...');
      cartDrawer.open();
    }
    
    // Method 2: Dispatch cart drawer event
    document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
    
    // Method 3: Try jQuery if available (some themes use it)
    if (window.$ && window.$.fancybox) {
      console.log('🛒 MyPad Cart: Trying jQuery fancybox cart...');
      setTimeout(() => {
        const cartLink = document.querySelector('a[href="/cart"]');
        if (cartLink) cartLink.click();
      }, 200);
    }
    
    console.log('🛒 MyPad Cart: ✅ Add to cart completed successfully');
    
    // Reset button after 2 seconds
    setTimeout(() => {
      button.textContent = originalText;
      button.disabled = false;
      button.style.background = '';
    }, 2000);
    
  } catch (error) {
    console.error('🛒 MyPad Cart: ❌ Error adding to cart:', error);
    console.error('🛒 MyPad Cart: Error details:', {
      message: error.message,
      stack: error.stack
    });
    
    // Error feedback
    button.textContent = 'Fehler - Nochmal versuchen';
    button.style.background = '#f44336';
    
    // Reset button after 3 seconds
    setTimeout(() => {
      button.textContent = originalText;
      button.disabled = false;
      button.style.background = '';
    }, 3000);
  }
}; 